import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { StopCard } from '@routific/hive';
import styled from 'styled-components';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import DeletingCard from '../../../../../shared/DeletingCard';
import { RouteTimelineDtoModelType } from '../../../../../../store/mst/models';
import { useStore } from '../../../../../../store/mst';
import TimelineDot from '../../../../../shared/TimelineDot';
import { IColor } from '../../../../../../types/IColor';
import OrderCard from './OrderCard';
import StopCardHeader, { getLabeledTimeDisplay } from './StopCardHeader';

interface Props {
  event: RouteTimelineDtoModelType;
  isDraggable?: boolean;
  isDragging?: boolean;
  isOtherDragging?: boolean;
  routeColor?: IColor;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  stateless?: boolean;
  hideDraggableIcon?: boolean;
  isClone?: boolean;
  isBreakdownLoading?: boolean;
  onDeliveryStopSelect?: (event: RouteTimelineDtoModelType, e: React.MouseEvent<HTMLDivElement>) => void;
}

const CardWrapper = styled.div<{ $isOtherDragging: boolean; $isDragging: boolean }>`
  cursor: ${({ $isOtherDragging, $isDragging }) => ($isOtherDragging || $isDragging ? 'grabbing' : 'default')};
`;

const VisitTimelineCard: React.FunctionComponent<Props> = ({
  event,
  isDraggable = false,
  isDragging = false,
  isOtherDragging = false,
  routeColor,
  stateless,
  hideDraggableIcon,
  dragHandleProps,
  isClone,
  isBreakdownLoading = false,
  onDeliveryStopSelect,
}) => {
  const { ui } = useStore();
  const { t } = useTranslation(['notifications', 'components']);
  const labeledTimeDisplay = getLabeledTimeDisplay(event, t);

  const firstOrder = event.orders?.[0];
  const hasAddressLoaded = !!firstOrder?.activeLocation?.hasLoaded('address');
  const address = hasAddressLoaded ? firstOrder?.displayLocation : null;

  const handleClickCard = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (onDeliveryStopSelect) {
      onDeliveryStopSelect(event, e);
      return;
    }
    ui.selectOrderAndStopByUuid(undefined, event.eventUuid);
  };

  const handleMouseDownCard = (e: React.MouseEvent<HTMLDivElement>) => {
    if (onDeliveryStopSelect && (e.shiftKey || e.ctrlKey || e.metaKey)) {
      e.preventDefault();
    }
  };

  if (event.orders?.some((o) => o.isDeleting)) {
    return <DeletingCard style={{ height: 144 }} data-testid="deleting-card" />;
  }

  const marker = (
    <TimelineDot
      eventType={event.eventType}
      sequence={event.stopSequence}
      status={event.currentStatus?.status}
      altColor={event?.orders?.find((o) => o.pendingColor)?.pendingColor}
      color={routeColor}
      loading={!!event.orders?.some((o) => o.isBeingModified)}
      eventSummary={event.eventSummary}
    />
  );

  return (
    <CardWrapper
      $isOtherDragging={isOtherDragging}
      $isDragging={isDragging}
      onClick={handleClickCard}
      onMouseDown={handleMouseDownCard}
      onMouseEnter={() => {
        if (!isOtherDragging) {
          ui.hoverRouteEvent(event.id, false, false);
        }
      }}
      onMouseLeave={() => {
        ui.hoverRouteEvent(undefined);
      }}
      data-cy-test-id={event.eventUuid}
      data-cy="visit-timeline-card"
      data-testid="visit-timeline-card"
    >
      <StopCard
        header={
          <StopCardHeader
            address={address}
            labeledTimeDisplay={labeledTimeDisplay}
            isLoading={isBreakdownLoading || !hasAddressLoaded}
          />
        }
        marker={marker}
        isComplete={!!event.isDeparted}
        isSelected={event.isSelected}
        isDragging={isDragging}
        isDraggable={isDraggable && !stateless && !hideDraggableIcon}
        forceDragHandle={isClone}
        dragHandleProps={{
          ...(dragHandleProps ?? {}),
          'data-cy': 'visit-drag-handle',
          'data-testid': 'visit-drag-handle',
          ...(isDragging || isOtherDragging ? { style: { cursor: 'grabbing' } } : {}),
        }}
      >
        {event.orders?.map((order, index) => (
          <OrderCard
            // Backward-compatible fallback for legacy/mock data where uuid may be absent.
            key={order.uuid ?? `${event.eventUuid}-order-${index}`}
            order={order}
            parentStopId={event.eventUuid}
            color={routeColor}
            isBreakdownLoading={isBreakdownLoading}
          />
        ))}
      </StopCard>
    </CardWrapper>
  );
};

export default observer(VisitTimelineCard);
